/**
 * MobileSheet — the shared bottom-sheet SHELL for phone surfaces.
 *
 * The chrome only: portal to document.body, dimmed backdrop, grabber, real
 * swipe-down-to-dismiss, slide up on open / down on close, safe-area padding.
 * Callers supply the contents.
 *
 * Extracted from MobileFileSheet (the Drive's ⋯ file sheet) so every phone
 * sheet dismisses with the SAME gesture and timing — a menu that looks like a
 * sheet but can't be swiped away is worse than a menu that doesn't pretend.
 * MobileFileSheet keeps its own copy for now (it interleaves drag state with
 * its quick-action strip); new sheets should use this.
 *
 * Portals outside any ThemedArea, so it re-applies the theme class itself
 * (CLAUDE.md theming rule) plus `ws-clear`.
 */
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'

// SHEET MOTION — 300ms on cubic-bezier(0.32,0.72,0,1), shared by every phone
// sheet in the app so they read as one system.
//
// Was 220ms on cubic-bezier(0.2,0.9,0.3,1), which ANIMATED and read as
// instant: measured off the live page, that curve covered 89% of the travel
// in the first 90ms and then spent 130ms imperceptibly settling, so the sheet
// appeared to snap into place. The fix is the CURVE more than the duration —
// this one keeps the motion visible through the middle of its travel, which is
// the part a slide is actually made of.
//
// The exit timeout below must equal this, or the sheet either unmounts
// mid-slide or hangs on screen after it.
const SHEET_MS = 300

export function MobileSheet({
  open, onClose, children,
}: {
  open: boolean
  onClose: () => void
  children: React.ReactNode
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)

  const [mounted, setMounted] = useState(false)
  const [shown, setShown] = useState(false)
  /**
   * Has the entry animation FINISHED?
   *
   * `.sheet-rise` is applied while `entering`, and `entering` was
   * `shown && dragY === 0` — which is true again the moment a below-threshold
   * drag springs back to 0. Re-adding an animation class RESTARTS the
   * keyframes, so releasing a 40px drag did not ease back to rest: the sheet
   * snapped to translateY(100%) and slid up again from the bottom edge, with
   * the scrim flashing from transparent to full.
   *
   * Latching on `animationend` means the class comes off once the entry has
   * played and never returns for the life of this sheet, so the drag
   * spring-back is left to the transition that is meant to handle it. Removing
   * the class cannot make it jump: the fill is `backwards`, so once the
   * animation ends the transform already comes from the inline style, which is
   * the same translateY(0).
   */
  const [entered, setEntered] = useState(false)
  const [dragY, setDragY] = useState(0)
  const dragging = useRef(false)
  const startY = useRef(0)
  const dragYRef = useRef(0)
  // The swipe-dismiss completion timer. Held in a ref and cleared on unmount:
  // it used to be a bare `window.setTimeout(onClose, 200)`, so if the parent
  // tore the sheet down inside that window (a realtime re-render, a route
  // change, its own `open=false`) the callback still fired against a dead
  // component. Harmless for today's setState-only `onClose`s — but this is a
  // shared primitive, and the next consumer whose `onClose` mutates or
  // navigates would get it fired after teardown.
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(dismissTimer.current), [])

  useEffect(() => {
    if (open) {
      setMounted(true); setDragY(0); dragYRef.current = 0
      // Reset `shown` HERE rather than relying on the close having done it.
      //
      // The slide-up only plays if the sheet paints OFF-SCREEN first, which
      // needs `shown === false` at this moment. Every close does set it false,
      // so the open path worked by depending on the close path — and anything
      // that leaves it true costs the animation silently and only on the
      // SECOND open, which is a horrible bug to find: the first open after a
      // reload is always correct.
      //
      // Three ways it can be true here. The parent swaps `file` from one row
      // straight to another with no null in between; a close is interrupted
      // before its state lands; or — the one that actually bites in dev —
      // Fast Refresh re-runs this module while PRESERVING component state, so
      // an edit to this file mid-session can leave `shown` stuck open.
      //
      // Setting it false and back to true across the two frames below is what
      // the double rAF is already for, so this costs nothing and makes the
      // entry animation independent of whatever came before it.
      setEntered(false)
      // Latch `entered` on a TIMER, not on `animationend`.
      //
      // `animationend` only fires if the animation runs to completion, and
      // there are two ways it does not. A drag that starts mid-flight removes
      // `.sheet-rise` (because `entering` requires `dragY === 0`), so the event
      // never arrives and `entered` stays false — then the spring-back re-adds
      // the class and REPLAYS the entry, which is the exact bug this latch was
      // added to prevent, just inside a 300ms window instead of always. And
      // under `prefers-reduced-motion` the animation is `none`, so it never
      // fires at all and the class would stay applied for the sheet's whole
      // life.
      //
      // A timer fires regardless of whether anything animated, which is the
      // property needed here: after SHEET_MS the entry is over by definition.
      const enteredTimer = setTimeout(() => setEntered(true), SHEET_MS)
      setShown(false)
      // Double rAF: paint the off-screen frame BEFORE flipping `shown`, or the
      // slide-up lands in the same paint and the animation is skipped.
      let r2 = 0
      const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setShown(true)) })
      return () => { cancelAnimationFrame(r1); cancelAnimationFrame(r2); clearTimeout(enteredTimer) }
    }
    setShown(false)
    const id = setTimeout(() => setMounted(false), SHEET_MS)
    return () => clearTimeout(id)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Pointer events cover touch AND mouse, so the gesture works in DevTools too.
  const onPointerDown = (e: React.PointerEvent) => {
    // The sheet is not draggable until it has finished arriving.
    //
    // This started as `setEntered(true)` here — latching the entry closed the
    // moment a finger landed, so a drag begun mid-flight could not replay the
    // slide on release. It fixed the replay and bought a smaller glitch: the
    // fill is `backwards`, so removing `.sheet-rise` mid-animation reverts the
    // transform to its resting value at once, and `dragging` has already
    // suppressed the transition — the sheet TELEPORTED the last third of its
    // travel instead of continuing to slide.
    //
    // Refusing the gesture for those 300ms removes both. There is no drag to
    // replay from and nothing to teleport, the timer is then the only thing
    // that needs to latch `entered`, and one invariant is back to having one
    // mechanism. It is also what a platform sheet does: gestures do not take
    // during the presentation animation.
    if (!entered) return
    dragging.current = true; startY.current = e.clientY; dragYRef.current = 0
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!dragging.current) return
    const dy = Math.max(0, e.clientY - startY.current)
    dragYRef.current = dy; setDragY(dy)
  }
  const onPointerUp = () => {
    if (!dragging.current) return
    dragging.current = false
    if (dragYRef.current > 90) {
      // Finish the gesture from where the finger left it (transition is back on
      // now), THEN unmount — so it slides out rather than snapping away.
      dragYRef.current = 0
      setDragY(window.innerHeight)
      clearTimeout(dismissTimer.current)
      dismissTimer.current = setTimeout(onClose, SHEET_MS)
    } else {
      setDragY(0); dragYRef.current = 0
    }
  }

  if (!mounted) return null

  /** Entry is a keyframe, exit and drag stay transitions — see the long note
   *  in MobileFileSheet, which had the same race and the same fix. */
  const entering = shown && dragY === 0 && !entered
  const transform = dragY > 0 ? `translateY(${dragY}px)` : (shown ? 'translateY(0)' : 'translateY(100%)')
  const backdropOpacity = shown ? Math.max(0, 1 - dragY / 400) : 0

  return createPortal(
    <div className={`${themeClass} ws-clear fixed inset-0 z-[200] flex flex-col justify-end`}>
      <div
        className={`sheet-motion absolute inset-0 bg-black/60 ${entering ? 'sheet-scrim-in' : ''}`}
        style={{ opacity: backdropOpacity, transition: dragging.current ? 'none' : `opacity ${SHEET_MS}ms ease-out` }}
        onClick={onClose}
      />
      <div
        className={`sheet-motion relative themed-bg-secondary rounded-t-2xl pb-[max(env(safe-area-inset-bottom),0.75rem)] shadow-2xl ${
          entering ? 'sheet-rise' : ''
        }`}
        style={{ transform, transition: dragging.current ? 'none' : `transform ${SHEET_MS}ms cubic-bezier(0.32,0.72,0,1)` }}
      >
        {/* The grabber is the drag surface — a real gesture, not decoration. */}
        <div
          className="cursor-grab touch-none active:cursor-grabbing pt-2.5 pb-1"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="mx-auto h-1 w-9 rounded-full themed-bg-tertiary" />
        </div>
        <div className="max-h-[70vh] overflow-y-auto overscroll-contain px-2 pb-2">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
